import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../db';

import { errorMessage } from '../utils/errors';
import { syncvalue } from '../utils/datetime';
import { syncRecordsets, handleRemoteDeletions } from '../utils/sync';

const template = () => {
  const record = {
    id: null,
    facility_id: null,
    name: '',
  };

  return { ...record };
};

const transformFromDb = (inputRecord) => {
  return { ...inputRecord };
};

const transformToDb = (inputRecord) => {
  const temp = { ...inputRecord };
  return { location: temp };
};

const toDexie = (inputRecord) => {
  return { ...inputRecord };
};

const fromDexie = (inputRecord) => {
  return { ...inputRecord };
};

const newLocation = () => {
  return template();
};

const listLocations = async () => {
  try {
    const temp = await db().locations.toCollection().toArray();
    return temp.map((cur) => fromDexie(cur));
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const listLocationsBySync = async (lastSync) => {
  try {
    const temp = await db().locations.where('sync').aboveOrEqual(lastSync).toArray();
    return temp.map((cur) => fromDexie(cur));
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getLocation = async (id) => {
  try {
    const temp = await db().locations.where('id').equals(id).first();
    return temp ? fromDexie(temp) : undefined;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchLocations = async () => {
  try {
    const results = await supabase.from('locations').select('*');
    if (results.error) throw new Error(`Error ${results.error.code}: ${results.error.message}`);
    if (results.data !== null) {
      return results.data.map((cur) => transformFromDb(cur));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchLocationsBySync = async (lastSync) => {
  try {
    const results = await supabase.from('locations').select('*').gte('sync', lastSync);
    if (results.error) throw new Error(`Error ${results.error.code}: ${results.error.message}`);
    if (results.data !== null) {
      return results.data.map((cur) => transformFromDb(cur));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const writeLocationToDexie = async (inputRecord) => {
  try {
    const cleanRecord = toDexie(inputRecord);
    const id = await db().locations.put(cleanRecord);
    return id;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const writeLocationToSupabase = async (inputRecord, facilityId) => {
  try {
    inputRecord.facility_id = inputRecord.facility_id || facilityId;
    const { location } = transformToDb(inputRecord);

    const results = await supabase.from('locations').upsert(location);
    if (results.error) throw new Error(errorMessage(results.error));
    return { results, id: location.id };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const dexieDeleteLocation = async (locationId) => {
  try {
    return await db().locations.delete(locationId);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const supabaseDeleteLocation = async (locationId) => {
  try {
    const results = await supabase.from('locations').delete().eq('id', locationId);
    if (results.error) throw new Error(errorMessage(results.error));
    return results;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const deleteLocation = async (locationId, isOnline) => {
  try {
    if (!locationId) throw new Error('locationId is required');
    if (!isOnline) throw new Error('Location deletion requires an online connection');

    const supabaseResults = await supabaseDeleteLocation(locationId);
    const dexieResults = await dexieDeleteLocation(locationId);

    return { supabaseResults, dexieResults, id: locationId };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const saveLocation = async (inputRecord, facilityId, isOnline) => {
  const syncValue = syncvalue();

  inputRecord.id = inputRecord.id || uuidv4();
  inputRecord.facility_id = inputRecord.facility_id || facilityId;
  inputRecord.sync = syncValue;

  const dexieResults = await writeLocationToDexie(inputRecord);
  const supabaseResults = isOnline
    ? await writeLocationToSupabase(inputRecord, facilityId)
    : { status: 'offline' };
  return { dexieResults, supabaseResults, id: inputRecord.id };
};

const sync = async ({ facilityId, lastSync = null }) => {
  const deletedRecords = await handleRemoteDeletions({
    tableName: 'locations',
    listFunc: listLocations,
    supabase,
    dbTable: db().locations,
  });
  console.log('deletedRecords', deletedRecords);

  const { remoteData, localData } = lastSync ? await syncPartialRecordSets(lastSync) : await syncFullRecordSets();
  const results = await syncRecordsets({
    remoteData,
    localData,
    remoteWriteFunc: writeLocationToSupabase,
    localWriteFunc: writeLocationToDexie,
    remoteWriteFuncArgs: [facilityId],
  });
  return results;
};

const syncFullRecordSets = async () => {
  const remoteData = (await fetchLocations()) || [];
  const localData = (await listLocations()) || [];

  return { remoteData, localData };
};

const syncPartialRecordSets = async (lastSync) => {
  const remoteData = (await fetchLocationsBySync(lastSync)) || [];
  const localData = (await listLocationsBySync(lastSync)) || [];

  return { remoteData, localData };
};

const truncateDexie = async () => {
  return await db().locations.clear();
};

export {
  newLocation,
  listLocations,
  getLocation,
  fetchLocations,
  saveLocation,
  deleteLocation,
  sync,
  truncateDexie,
};
